/**
 * TeamStrength Hub - Profile Page
 * Editable profile with photo upload, position, and strength selection
 */
import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { profileApi, modelApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Sparkles, Lightbulb, RefreshCw, Globe, 
  Briefcase, Mail, Building2, Camera, Pencil, Save, X,
  Check, GripVertical
} from 'lucide-react';

const ProfilePage = () => {
  const { user, currentOrg, checkAuth } = useAuth();
  const fileInputRef = useRef(null);
  
  // State - use user's preferred language as default
  const [language, setLanguage] = useState(user?.preferred_language || 'de');
  const [strengths, setStrengths] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  // Edit mode states
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editJobTitle, setEditJobTitle] = useState('');
  const [editPicture, setEditPicture] = useState('');
  const [editLanguage, setEditLanguage] = useState('de');
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Strength selection dialog
  const [showStrengthDialog, setShowStrengthDialog] = useState(false);
  const [availableTalents, setAvailableTalents] = useState([]);
  const [selectedStrengths, setSelectedStrengths] = useState([]);
  const [savingStrengths, setSavingStrengths] = useState(false);

  // Load profile data
  useEffect(() => {
    const loadData = async () => {
      if (!currentOrg?.org_id) return;
      
      setLoading(true);
      try {
        const strengthsRes = await profileApi.getStrengths(currentOrg.org_id);
        setStrengths(strengthsRes.data || []);
        
        const cachedRes = await profileApi.getAnalysis(currentOrg.org_id, language);
        if (cachedRes.data && cachedRes.data.summary) {
          setAnalysis(cachedRes.data);
        }
      } catch (error) {
        console.error('Failed to load profile data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [currentOrg?.org_id, language]);

  // Initialize edit values when user changes
  useEffect(() => {
    if (user) {
      setEditName(user.name || '');
      setEditJobTitle(user.job_title || '');
      setEditPicture(user.picture || '');
      setEditLanguage(user.preferred_language || 'de');
      // Also update display language from user preference
      if (user.preferred_language) {
        setLanguage(user.preferred_language);
      }
    }
  }, [user]);

  // Load talents for strength selection
  const loadTalents = async () => {
    if (!currentOrg?.org_id) return;
    
    try {
      const modelRes = await modelApi.get(currentOrg.org_id);
      if (modelRes.data?.talents) {
        // Add category info to talents
        const categories = modelRes.data.categories || [];
        const categoryMap = categories.reduce((acc, c) => {
          acc[c.category_id] = c;
          return acc;
        }, {});
        
        const talentsWithCategory = modelRes.data.talents.map(t => ({
          ...t,
          category_name: categoryMap[t.category_id]?.name || 'Andere',
          category_color: categoryMap[t.category_id]?.color || '#6B7280'
        }));
        
        setAvailableTalents(talentsWithCategory);
        
        // Pre-select current strengths
        const currentTalentIds = strengths.map(s => s.talent_id);
        const preSelected = talentsWithCategory
          .filter(t => currentTalentIds.includes(t.talent_id))
          .sort((a, b) => {
            const rankA = strengths.find(s => s.talent_id === a.talent_id)?.rank || 999;
            const rankB = strengths.find(s => s.talent_id === b.talent_id)?.rank || 999;
            return rankA - rankB;
          });
        setSelectedStrengths(preSelected);
      }
    } catch (error) {
      console.error('Failed to load talents:', error);
      toast.error('Fehler beim Laden der Stärken');
    }
  };

  // Generate AI analysis
  const generateAnalysis = async () => {
    if (!currentOrg?.org_id) return;
    
    setGenerating(true);
    try {
      const response = await profileApi.generateAnalysis({
        org_id: currentOrg.org_id,
        language
      });
      setAnalysis(response.data);
      toast.success(language === 'de' ? 'Analyse erstellt!' : 'Analysis generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate analysis');
    } finally {
      setGenerating(false);
    }
  };

  // Toggle language
  const toggleLanguage = () => {
    const newLang = language === 'de' ? 'en' : 'de';
    setLanguage(newLang);
    setAnalysis(null);
  };

  // Handle photo upload
  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte wähle ein Bild aus');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Bild darf maximal 5MB groß sein');
      return;
    }
    
    setUploadingPhoto(true);
    try {
      // Convert to base64 for simple storage
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditPicture(reader.result);
        setUploadingPhoto(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Fehler beim Hochladen');
      setUploadingPhoto(false);
    }
  };

  // Save profile changes
  const saveProfile = async () => {
    setSaving(true);
    try {
      await profileApi.update({
        name: editName,
        job_title: editJobTitle,
        picture: editPicture,
        preferred_language: editLanguage
      });
      await checkAuth();
      setLanguage(editLanguage); // Update display language
      toast.success(editLanguage === 'de' ? 'Profil gespeichert!' : 'Profile saved!');
      setIsEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditName(user?.name || '');
    setEditJobTitle(user?.job_title || '');
    setEditPicture(user?.picture || '');
    setEditLanguage(user?.preferred_language || 'de');
    setIsEditing(false);
  };

  // Toggle strength selection
  const toggleStrength = (talent) => {
    const isSelected = selectedStrengths.some(s => s.talent_id === talent.talent_id);
    
    if (isSelected) {
      setSelectedStrengths(prev => prev.filter(s => s.talent_id !== talent.talent_id));
    } else {
      if (selectedStrengths.length >= 10) {
        toast.error('Maximal 10 Stärken erlaubt');
        return;
      }
      setSelectedStrengths(prev => [...prev, talent]);
    }
  };

  // Move strength in ranking
  const moveStrength = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= selectedStrengths.length) return;
    
    const newStrengths = [...selectedStrengths];
    [newStrengths[index], newStrengths[newIndex]] = [newStrengths[newIndex], newStrengths[index]];
    setSelectedStrengths(newStrengths);
  };

  // Save strengths
  const saveStrengths = async () => {
    if (selectedStrengths.length < 5) {
      toast.error('Bitte wähle mindestens 5 Stärken');
      return;
    }
    
    setSavingStrengths(true);
    try {
      await profileApi.saveStrengths({
        org_id: currentOrg.org_id,
        strengths: selectedStrengths.map((s, i) => ({
          talent_id: s.talent_id,
          rank: i + 1
        }))
      });
      
      // Reload strengths
      const strengthsRes = await profileApi.getStrengths(currentOrg.org_id);
      setStrengths(strengthsRes.data || []);
      
      // Clear cached analysis since strengths changed
      setAnalysis(null);
      
      toast.success('Stärken gespeichert!');
      setShowStrengthDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingStrengths(false);
    }
  };

  const labels = {
    de: {
      title: 'Mein Profil',
      subtitle: 'Bearbeite dein Profil und deine Stärken',
      strengths: 'Meine Top-Stärken',
      analysis: 'KI-Analyse',
      tips: 'Praktische Tipps',
      generate: 'Analyse generieren',
      regenerate: 'Neu generieren',
      noStrengths: 'Noch keine Stärken ausgewählt',
      selectStrengths: 'Stärken auswählen',
      editStrengths: 'Stärken bearbeiten',
      generating: 'Wird generiert...',
      position: 'Position',
      email: 'E-Mail',
      organization: 'Organisation',
      edit: 'Bearbeiten',
      save: 'Speichern',
      cancel: 'Abbrechen',
      uploadPhoto: 'Foto hochladen',
      name: 'Name',
      language: 'Bevorzugte Sprache',
      german: 'Deutsch',
      english: 'Englisch'
    },
    en: {
      title: 'My Profile',
      subtitle: 'Edit your profile and strengths',
      strengths: 'My Top Strengths',
      analysis: 'AI Analysis',
      tips: 'Practical Tips',
      generate: 'Generate Analysis',
      regenerate: 'Regenerate',
      noStrengths: 'No strengths selected yet',
      selectStrengths: 'Select Strengths',
      editStrengths: 'Edit Strengths',
      generating: 'Generating...',
      position: 'Position',
      email: 'Email',
      organization: 'Organization',
      edit: 'Edit',
      save: 'Save',
      cancel: 'Cancel',
      uploadPhoto: 'Upload Photo',
      name: 'Name',
      language: 'Preferred Language',
      german: 'German',
      english: 'English'
    }
  };

  const t = labels[language];

  // Group talents by category for selection dialog
  const talentsByCategory = availableTalents.reduce((acc, talent) => {
    const catId = talent.category_id;
    if (!acc[catId]) {
      acc[catId] = {
        category_id: catId,
        name: talent.category_name || 'Andere',
        color: talent.category_color || '#6B7280',
        talents: []
      };
    }
    acc[catId].talents.push(talent);
    return acc;
  }, {});

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header with Language Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>
            {t.title}
          </h1>
          <p className="text-slate-500 dark:text-slate-400">{t.subtitle}</p>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={toggleLanguage}
          className="gap-2"
          data-testid="language-toggle"
        >
          <Globe className="w-4 h-4" />
          {language === 'de' ? 'English' : 'Deutsch'}
        </Button>
      </div>

      {/* Profile Card - Editable */}
      <Card className="overflow-hidden">
        <div className="h-40 bg-gradient-to-br from-violet-600 via-purple-500 to-indigo-600 relative">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />
          <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/30 to-transparent" />
          
          {/* Name and info on the gradient background */}
          <div className="absolute bottom-4 left-40 right-4 flex items-end justify-between">
            {!isEditing && (
              <div>
                <h2 className="text-2xl font-bold text-white drop-shadow-lg" style={{ fontFamily: 'Manrope' }}>
                  {user?.name}
                </h2>
                <div className="flex flex-wrap gap-4 mt-1 text-sm text-white/90">
                  {user?.job_title && (
                    <span className="flex items-center gap-1">
                      <Briefcase className="w-4 h-4" />
                      {user.job_title}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {user?.email}
                  </span>
                  {currentOrg && (
                    <span className="flex items-center gap-1">
                      <Building2 className="w-4 h-4" />
                      {currentOrg.name}
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {/* Edit button on gradient */}
            {!isEditing && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setIsEditing(true)}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                data-testid="profile-edit-btn"
              >
                <Pencil className="w-4 h-4 mr-1" />
                {t.edit}
              </Button>
            )}
          </div>
        </div>
        <CardContent className="relative pt-0">
          <div className="flex flex-col sm:flex-row items-start gap-4 -mt-14">
            {/* Avatar with upload option */}
            <div className="relative group">
              <Avatar className="w-28 h-28 border-4 border-white dark:border-slate-900 shadow-xl">
                <AvatarImage 
                  src={isEditing ? editPicture : user?.picture} 
                  className="object-cover"
                />
                <AvatarFallback className="bg-gradient-to-br from-violet-500 to-purple-600 text-white text-3xl font-bold">
                  {(isEditing ? editName : user?.name)?.charAt(0)?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              
              {isEditing && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  <Camera className="w-8 h-8 text-white" />
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoUpload}
              />
            </div>
            
            {/* Edit form - only shown in edit mode */}
            {isEditing && (
              <div className="flex-1 pt-16">
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="name" className="text-xs text-slate-500">{t.name}</Label>
                    <Input
                      id="name"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="mt-1"
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="jobTitle" className="text-xs text-slate-500">{t.position}</Label>
                    <Input
                      id="jobTitle"
                      value={editJobTitle}
                      onChange={(e) => setEditJobTitle(e.target.value)}
                      placeholder="z.B. Software Developer"
                      className="mt-1"
                      data-testid="profile-jobtitle-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="language" className="text-xs text-slate-500">{t.language}</Label>
                    <div className="flex gap-2 mt-1">
                      <Button
                        type="button"
                        variant={editLanguage === 'de' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setEditLanguage('de')}
                        className={editLanguage === 'de' ? 'bg-violet-600 hover:bg-violet-700' : ''}
                        data-testid="language-de-btn"
                      >
                        🇩🇪 {t.german}
                      </Button>
                      <Button
                        type="button"
                        variant={editLanguage === 'en' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setEditLanguage('en')}
                        className={editLanguage === 'en' ? 'bg-violet-600 hover:bg-violet-700' : ''}
                        data-testid="language-en-btn"
                      >
                        🇬🇧 {t.english}
                      </Button>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={cancelEditing}
                      disabled={saving}
                    >
                      <X className="w-4 h-4 mr-1" />
                      {t.cancel}
                    </Button>
                    <Button
                      size="sm"
                      onClick={saveProfile}
                      disabled={saving}
                      className="bg-violet-600 hover:bg-violet-700"
                      data-testid="profile-save-btn"
                    >
                      <Save className="w-4 h-4 mr-1" />
                      {saving ? '...' : t.save}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Strengths Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Sparkles className="w-5 h-5 text-violet-600" />
              {t.strengths}
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                loadTalents();
                setShowStrengthDialog(true);
              }}
              data-testid="edit-strengths-btn"
            >
              <Pencil className="w-4 h-4 mr-1" />
              {strengths.length > 0 ? t.editStrengths : t.selectStrengths}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex flex-wrap gap-2">
              {[1,2,3,4,5].map(i => (
                <Skeleton key={i} className="h-8 w-24 rounded-full" />
              ))}
            </div>
          ) : strengths.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {strengths.map((s, index) => (
                <Badge 
                  key={s.strength_id || s.talent_id || index}
                  variant="secondary"
                  className="px-3 py-1.5 text-sm font-medium"
                  style={{ 
                    backgroundColor: s.talent?.color ? `${s.talent.color}20` : undefined,
                    borderColor: s.talent?.color,
                    color: s.talent?.color
                  }}
                >
                  <span className="w-5 h-5 rounded-full bg-current/20 flex items-center justify-center mr-2 text-xs font-bold">
                    {index + 1}
                  </span>
                  {s.talent?.name || s.name || `Strength ${index + 1}`}
                </Badge>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Sparkles className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-4">{t.noStrengths}</p>
              <Button 
                onClick={() => {
                  loadTalents();
                  setShowStrengthDialog(true);
                }} 
                variant="outline"
              >
                {t.selectStrengths}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Analysis Section */}
      {strengths.length > 0 && (
        <Card className="border-violet-200 dark:border-violet-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-violet-600" />
                </div>
                {t.analysis}
              </CardTitle>
              
              <Button
                onClick={generateAnalysis}
                disabled={generating}
                size="sm"
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="generate-analysis-btn"
              >
                {generating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    {t.generating}
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {analysis ? t.regenerate : t.generate}
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {generating ? (
              <div className="space-y-4">
                <Skeleton className="h-20 w-full" />
                <div className="grid gap-4 md:grid-cols-3">
                  {[1,2,3].map(i => (
                    <Skeleton key={i} className="h-32 w-full" />
                  ))}
                </div>
              </div>
            ) : analysis ? (
              <>
                <div className="p-4 rounded-xl bg-gradient-to-br from-violet-50 to-cyan-50 dark:from-violet-900/20 dark:to-cyan-900/20 border border-violet-100 dark:border-violet-800">
                  <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                    {analysis.summary}
                  </p>
                </div>

                {analysis.tips && analysis.tips.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                      <Lightbulb className="w-5 h-5 text-amber-500" />
                      {t.tips}
                    </h3>
                    <div className="grid gap-4 md:grid-cols-3">
                      {analysis.tips.map((tip, index) => (
                        <Card key={index} className="bg-white dark:bg-slate-800 hover:shadow-md transition-shadow">
                          <CardContent className="pt-6">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 flex items-center justify-center mb-3">
                              <span className="text-lg font-bold text-amber-600">{index + 1}</span>
                            </div>
                            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                              {tip}
                            </p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-violet-400" />
                </div>
                <p className="text-slate-500 dark:text-slate-400 mb-4">
                  {language === 'de' 
                    ? 'Klicke auf "Analyse generieren" um deine persönliche Stärkenanalyse zu erhalten.'
                    : 'Click "Generate Analysis" to get your personal strength analysis.'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Strength Selection Dialog */}
      <Dialog open={showStrengthDialog} onOpenChange={setShowStrengthDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {language === 'de' ? 'Stärken auswählen' : 'Select Strengths'}
            </DialogTitle>
            <p className="text-sm text-slate-500">
              {language === 'de' 
                ? `Wähle 5-10 Stärken und ordne sie nach Wichtigkeit. (${selectedStrengths.length}/10 ausgewählt)`
                : `Select 5-10 strengths and rank them by importance. (${selectedStrengths.length}/10 selected)`}
            </p>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            <div className="grid md:grid-cols-2 gap-6 p-4">
              {/* Available Talents */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700 dark:text-slate-300">
                  {language === 'de' ? 'Verfügbare Stärken' : 'Available Strengths'}
                </h3>
                <div className="space-y-4 max-h-[50vh] overflow-auto pr-2">
                  {Object.values(talentsByCategory).map((category) => (
                    <div key={category.category_id}>
                      <div 
                        className="text-xs font-semibold uppercase tracking-wide px-2 py-1 rounded mb-2"
                        style={{ 
                          backgroundColor: `${category.color}20`,
                          color: category.color
                        }}
                      >
                        {category.name}
                      </div>
                      <div className="space-y-1">
                        {category.talents.map((talent) => {
                          const isSelected = selectedStrengths.some(s => s.talent_id === talent.talent_id);
                          return (
                            <button
                              key={talent.talent_id}
                              onClick={() => toggleStrength(talent)}
                              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between ${
                                isSelected 
                                  ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300'
                                  : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                              }`}
                            >
                              <span>{talent.name}</span>
                              {isSelected && <Check className="w-4 h-4" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Selected Strengths with ranking */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700 dark:text-slate-300">
                  {language === 'de' ? 'Dein Ranking' : 'Your Ranking'}
                </h3>
                <div className="space-y-2">
                  {selectedStrengths.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-8">
                      {language === 'de' 
                        ? 'Wähle Stärken aus der Liste links'
                        : 'Select strengths from the list on the left'}
                    </p>
                  ) : (
                    selectedStrengths.map((strength, index) => (
                      <div 
                        key={strength.talent_id}
                        className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                      >
                        <div className="w-8 h-8 rounded-full bg-violet-600 text-white flex items-center justify-center font-bold text-sm">
                          {index + 1}
                        </div>
                        <span className="flex-1 font-medium text-slate-700 dark:text-slate-300">
                          {strength.name}
                        </span>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveStrength(index, -1)}
                            disabled={index === 0}
                            className="h-8 w-8 p-0"
                          >
                            ↑
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moveStrength(index, 1)}
                            disabled={index === selectedStrengths.length - 1}
                            className="h-8 w-8 p-0"
                          >
                            ↓
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleStrength(strength)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowStrengthDialog(false)}>
              {t.cancel}
            </Button>
            <Button 
              onClick={saveStrengths}
              disabled={selectedStrengths.length < 5 || savingStrengths}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="save-strengths-btn"
            >
              {savingStrengths ? '...' : t.save}
              {selectedStrengths.length < 5 && ` (${5 - selectedStrengths.length} mehr)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
